class CosBackendCollectionFilter extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.endpoint = this.dataset.endpoint;
    this.collection = this.dataset.collection;
    this.limit = this.dataset.limit || '24';
    this.page = 1;
    this.isFetching = false;
    this.hasNextPage = false;
    this.results = this.querySelector('[data-product-results]');
    this.count = this.querySelector('[data-result-count]');
    this.loadMore = this.querySelector('[data-load-more]');
    this.facets = this.querySelector('[data-facets]');
    this.form = this.querySelector('[data-filter-form]');
    this.toggle = this.querySelector('[data-filter-toggle]');
    this.mobile = window.matchMedia('(max-width: 749px)');

    if (!this.endpoint || !this.collection || !this.results || !this.form) return;

    this.applyUrlState();
    this.appliedFilters = this.readFormFilters();
    const scheduleRefresh = this.debounce(() => {
      if (!this.mobile.matches) this.refresh();
    }, 300);
    this.form.addEventListener('input', () => {
      this.draftDirty = true;
      if (!this.mobile.matches) scheduleRefresh();
    });
    this.form.addEventListener('change', () => {
      this.draftDirty = true;
      if (!this.mobile.matches) this.refresh();
    });
    this.querySelector('[name="sort_by"]') ?.addEventListener('change', () => this.refresh());
    this.toggle ?.addEventListener('click', () => this.setFiltersOpen(!this.hasAttribute('data-filters-open')));
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.appliedFilters = this.readFormFilters();
      this.draftDirty = false;
      this.setFiltersOpen(false);
      if (this.mobile.matches) this.toggle ?.focus();
      this.refresh();
    });
    this.form.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.mobile.matches) {
        this.setFiltersOpen(false);
        this.toggle ?.focus();
      }
    });
    this.mobile.addEventListener('change', () => {
      // Discard unsubmitted mobile edits when returning to instant desktop filters.
      this.restoreFormFilters(this.appliedFilters);
      this.draftDirty = false;
      this.setFiltersOpen(false);
    });
    this.querySelector('[data-clear-filters]') ?.addEventListener('click', () => this.clear());
    this.loadMore ?.addEventListener('click', () => this.nextPage());
    this.setupInfiniteScroll();
    window.addEventListener('popstate', () => {
      this.applyUrlState();
      this.appliedFilters = this.readFormFilters();
      this.draftDirty = false;
      this.refresh({
        updateUrl: false
      });
    });

    this.refresh({
      updateUrl: false
    });
  }

  setFiltersOpen(open) {
    this.toggleAttribute('data-filters-open', open);
    this.toggle ?.setAttribute('aria-expanded', String(open));
  }

  readFormFilters() {
    return new URLSearchParams([...new FormData(this.form)].filter(([, value]) => String(value).trim()));
  }

  restoreFormFilters(params) {
    this.form.querySelectorAll('input').forEach((field) => {
      if (!field.name) return;
      if (field.type === 'checkbox' || field.type === 'radio') field.checked = params.getAll(field.name).includes(field.value);
      else field.value = params.get(field.name) || '';
    });
  }

  applyUrlState() {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('availability')) params.set('availability', 'in-stock');

    this.querySelectorAll('input, select').forEach((field) => {
      if (!field.name) return;

      if (field.name === 'sort_by') {
        const requestedSort = params.get(field.name);
        const options = Array.from(field.options);
        field.value = options.some((option) => option.value === requestedSort)
          ? requestedSort
          : options[0].value;
        return;
      }

      if (field.type === 'checkbox' || field.type === 'radio') {
        field.checked = params.getAll(field.name).includes(field.value);
        return;
      }

      field.value = params.get(field.name) || '';
    });
  }

  get params() {
    const params = new URLSearchParams({
      collection: this.collection,
      limit: this.limit,
      page: String(this.page),
    });

    const filters = this.mobile.matches ? this.appliedFilters : this.readFormFilters();
    for (const [key, value] of filters) params.append(key, value);
    const sort = this.querySelector('[name="sort_by"]') ?.value;
    if (sort) params.set('sort_by', sort);

    return params;
  }

  refresh(options = {}) {
    if (!this.mobile.matches) {
      this.appliedFilters = this.readFormFilters();
      this.draftDirty = false;
    }
    this.page = 1;
    if (options.updateUrl !== false) this.writeUrl();
    return this.fetchProducts(false);
  }

  nextPage() {
    if (this.isFetching || !this.hasNextPage) return Promise.resolve();
    this.page += 1;
    return this.fetchProducts(true);
  }

  async fetchProducts(append) {
    if (append && this.isFetching) return;
    this.requestController ?.abort();
    const controller = new AbortController();
    this.requestController = controller;

    this.isFetching = true;
    this.classList.add('is-loading');
    this.setAttribute('aria-busy', 'true');
    if (this.loadMore) {
      this.loadMore.disabled = true;
      this.loadMore.textContent = 'Loading...';
    }

    try {
      const response = await fetch(`${this.endpoint}?${this.params.toString()}`, {
        headers: {
          Accept: 'application/json'
        },
        signal: controller.signal,
      });
      // Shopify and hosting gateways can return HTML error pages, even when
      // the request explicitly accepts JSON.
      const contentType = response.headers.get('content-type') || '';
      if (!/\bapplication\/(?:[\w.-]+\+)?json\b/i.test(contentType)) {
        throw new Error('Products are temporarily unavailable. Please try again shortly.');
      }
      let payload;
      try {
        payload = await response.json();
      } catch (error) {
        if (error.name === 'AbortError') throw error;
        throw new Error('Products are temporarily unavailable. Please try again shortly.');
      }
      if (controller !== this.requestController) return;
      if (!response.ok || !payload || !Array.isArray(payload.products) || !payload.pagination) {
        throw new Error('Products are temporarily unavailable. Please try again shortly.');
      }

      this.renderFilters(payload.filters);
      this.renderProducts(payload.products || [], append);
      this.renderPagination(payload.pagination);
    } catch (error) {
      if (controller !== this.requestController || error.name === 'AbortError') return;
      this.hasNextPage = false;
      if (append) this.page -= 1;
      this.results.querySelector('.cos-backend-filter__error')?.remove();
      const message = '<p class="cos-backend-filter__error" role="alert">Products are temporarily unavailable. Please try again shortly.</p>';
      if (append) this.results.insertAdjacentHTML('beforeend', message);
      else this.results.innerHTML = message;
      if (this.loadMore) this.loadMore.hidden = true;
    } finally {
      if (controller !== this.requestController) return;
      this.isFetching = false;
      this.classList.remove('is-loading');
      this.removeAttribute('aria-busy');
      if (this.loadMore) {
        this.loadMore.disabled = false;
        this.loadMore.textContent = 'Load more';
      }
    }
  }

  setupInfiniteScroll() {
    if (!this.loadMore || !('IntersectionObserver' in window)) return;

    this.infiniteScrollObserver ?.disconnect();
    this.infiniteScrollObserver = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) this.nextPage();
    }, {
      root: null,
      rootMargin: '600px 0px',
      threshold: 0,
    });
    this.infiniteScrollObserver.observe(this.loadMore);
  }

  renderFilters(filters) {
    if (!this.facets || !filters ?.facets) return;
    const draft = this.mobile.matches && this.draftDirty ? this.readFormFilters() : null;

    const focused = document.activeElement;
    const focusedKey = focused ?.name;
    const focusedValue = focused ?.value;

    this.facets.innerHTML = filters.facets
      .filter((facet) => facet.key !== 'vendor' && facet.source !== 'vendor' && facet.values.length)
      .map((facet) => `
        <fieldset class="cos-backend-filter__group">
          <legend>${this.escape(facet.label)}</legend>
          <div class="cos-backend-filter__options">
            ${facet.values.map((item) => this.renderCheckbox(facet.key, item)).join('')}
          </div>
        </fieldset>
      `)
      .join('');

    if (draft) this.restoreFormFilters(draft);

    if (focusedKey && focusedValue) {
      this.querySelector(`[name="${CSS.escape(focusedKey)}"][value="${CSS.escape(focusedValue)}"]`) ?.focus();
    }
  }

  renderCheckbox(name, item) {
    return `
      <label class="cos-backend-filter__option${item.count ? '' : ' is-disabled'}">
        <input
          name="${this.escape(name)}"
          type="checkbox"
          value="${this.escape(item.value)}"
          ${item.active ? 'checked' : ''}
          ${item.count ? '' : 'disabled'}
        >
        <span>${this.escape(item.label)}</span>
        <span>${item.count}</span>
      </label>
    `;
  }

  renderProducts(products, append) {
    const html = products.map((product) => {
      const onSale = Number(product.compareAtPrice) > Number(product.price);
      return `
      <article class="cos-backend-filter__card">
        <a href="${this.escape(product.url)}">
          <span class="cos-backend-filter__media">
            <img src="${this.escape(product.image || this.dataset.fallbackImage)}" alt="${this.escape(product.image ? product.imageAlt : 'Image unavailable')}" loading="lazy">
          </span>
          <span class="cos-backend-filter__title">${this.escape(product.title)}</span>
          <span class="cos-backend-filter__price">
            <span${onSale ? ' class="cos-backend-filter__sale-price" aria-label="Sale price"' : ''}>${this.money(product.price)} ${this.escape(this.dataset.currency || 'USD')}</span>
            ${onSale ? `<s class="cos-backend-filter__compare-price" aria-label="Original price">${this.money(product.compareAtPrice)} ${this.escape(this.dataset.currency || 'USD')}</s>` : ''}
          </span>
          ${onSale ? '<span class="cos-backend-filter__badge cos-backend-filter__badge--sale">SALE</span>' : ''}
          ${product.available ? '' : '<span class="cos-backend-filter__badge cos-backend-filter__badge--sold-out">Sold out</span>'}
        </a>
      </article>
    `;
    }).join('');

    if (append) this.results.insertAdjacentHTML('beforeend', html);
    else this.results.innerHTML = html || '<p class="cos-backend-filter__empty">No products found.</p>';
  }

  renderPagination(pagination) {
    if (!pagination) return;
    this.hasNextPage = Boolean(pagination.hasNextPage);

    if (this.count) {
      this.count.textContent = `${pagination.total} ${pagination.total === 1 ? 'item' : 'items'}`;
    }
    if (this.loadMore) this.loadMore.hidden = !this.hasNextPage;
  }

  clear() {
    this.form.reset();
    this.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach((input) => {
      input.checked = false;
    });
    this.draftDirty = true;
    if (!this.mobile.matches) this.refresh();
  }

  writeUrl() {
    const url = new URL(window.location.href);
    const next = this.params;
    next.delete('collection');
    next.delete('limit');
    next.delete('page');
    // Keep an explicit all-stock state so Clear all survives reload and history.
    if (!next.has('availability')) next.set('availability', 'all');
    url.search = next.toString();
    window.history.pushState({}, '', url.toString());
  }

  money(value) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: this.dataset.currency || 'USD',
      currencyDisplay: 'narrowSymbol',
    }).format(Number(value || 0));
  }

  debounce(fn, wait) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  escape(value) {
    const div = document.createElement('div');
    div.textContent = String(value ?? '');
    return div.innerHTML;
  }
}

if (!customElements.get('cos-backend-collection-filter')) {
  customElements.define('cos-backend-collection-filter', CosBackendCollectionFilter);
}
